import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Breadcrumb } from 'components/Breadcrumb';
import { PageHeading } from 'components/PageHeading';
import { SimpleSelect } from 'components/select/SimpleSelect';
import { useAuth } from 'contexts/auth-context';
import { roleToNumber } from 'src/app/auth/roles.client';
import { BUSINESS_UNIT_OPTIONS } from 'src/app/order/constants';
import { InternalUser, UserCombobox } from 'src/app/user/components/UserCombobox';
import { PageHeader } from '../../../components/PageHeader';
import { DelayedSpinner, Spinner } from '../../../components/Spinner';
import { Tabs } from '../../../components/tabs/Tabs';
import { TimeFrame } from '../../../components/time-frame/time-frame';
import { TimeFrameState } from '../../../components/time-frame/types';
import { BusinessUnit } from 'src/types/enums';
import { InternalRole } from '../../auth/endpoints/MeEndpoint.types';
import { DashboardProvider } from '../context';
import { useOrderStats } from '../models';
import { ErrorState } from '../../../components/error-state/ErrorState';

interface StatsProps {
  dealOwnerId: string | null;
  businessUnit: BusinessUnit | null;
  timeFrame: TimeFrameState;
  onFetchingChange: (fetching: boolean) => void;
}

const Statistics: React.FC<StatsProps> = (props) => {
  const { dealOwnerId, businessUnit, timeFrame, onFetchingChange } = props;
  const { me } = useAuth();
  const location = useLocation();
  const isUserActivityTab = location.pathname.includes('/stats/user-activity');
  const {
    data,
    error,
    isValidating: fetching,
    mutate: refetch,
  } = useOrderStats({
    dealOwnerId,
    businessUnit,
    startDate: timeFrame.startDate,
    endDate: timeFrame.endDate,
  });

  useEffect(() => {
    onFetchingChange(fetching);
  }, [fetching, onFetchingChange]);

  const meRoleNumber = roleToNumber(me.role);
  const adminRoleNumber = roleToNumber(InternalRole.InternalAdmin);

  const tabs = useMemo(() => {
    const _tabs = [];
    if (meRoleNumber >= adminRoleNumber) {
      _tabs.push(
        {
          title: 'Overzicht',
          path: 'overview',
        },
        {
          title: 'Boekhouding',
          path: 'accounting',
        },
        {
          title: 'Truckrapport',
          path: 'truck-report',
        },
        {
          title: 'Driver App',
          path: 'driver-app',
        },
        {
          title: 'Gebruikersactiviteit',
          path: 'user-activity',
        },
      );
    }

    _tabs.push({
      title: 'Sales',
      path: 'sales',
    });

    return _tabs;
  }, [meRoleNumber, adminRoleNumber]);

  if (!isUserActivityTab && error) {
    return <ErrorState error={error} onRetry={() => refetch()} variant="section" />;
  }

  if (!isUserActivityTab && !data) {
    return <DelayedSpinner size={8} />;
  }

  return (
    <DashboardProvider
      entries={data ?? []}
      refreshData={() => {
        refetch();
      }}
      dealOwnerID={dealOwnerId}
      businessUnit={businessUnit}
      timeFrame={timeFrame}
    >
      <Tabs items={tabs} />
    </DashboardProvider>
  );
};

export const StatisticsPage = () => {
  const { me } = useAuth();
  const location = useLocation();
  const isUserActivityTab = location.pathname.includes('/stats/user-activity');

  const [dealOwnerValue, setDealOwnerValue] = useState<InternalUser | null>(null);
  const [dealOwnerId, setDealOwnerId] = useState<null | string>(null);
  const [businessUnit, setBusinessUnit] = useState<null | BusinessUnit>(null);
  const [timeFrame, setTimeFrame] = useState<null | TimeFrameState>(null);
  const [isFetching, setIsFetching] = useState<boolean>(true);

  const meRoleNumber = roleToNumber(me.role);
  const adminRoleNumber = roleToNumber(InternalRole.InternalAdmin);

  return (
    <>
      <PageHeader title="Statistieken" />

      <PageHeading noMarginBottom leftSide={<Breadcrumb currentItem="Statistieken" />} />

      <div className="flex flex-col gap-4 p-7">
        <div className="card flex flex-wrap items-end gap-3 p-4">
          <TimeFrame onChange={setTimeFrame} />

          <div className="w-full max-w-56">
            <div className="label-text mb-1.5 w-full">Business Unit</div>
            <SimpleSelect
              onSelect={(value) => {
                if (!value) {
                  setBusinessUnit(null);
                } else {
                  setBusinessUnit(value.key as BusinessUnit);
                }
              }}
              selectedItem={BUSINESS_UNIT_OPTIONS.find((v) => v.key === businessUnit) ?? null}
              items={BUSINESS_UNIT_OPTIONS}
              isOptional={true}
            />
          </div>

          {meRoleNumber >= adminRoleNumber && (
            <div className="w-full max-w-64">
              <div className="label-text mb-1.5 w-full">Dealeigenaar</div>
              <UserCombobox
                value={dealOwnerValue}
                onChange={(v) => {
                  setDealOwnerValue(v);
                  setDealOwnerId(v?.id ?? null);
                }}
              />
            </div>
          )}

          {!isUserActivityTab && isFetching && (
            <div className="flex h-9 items-center">
              <Spinner size={4} />
            </div>
          )}
        </div>

        {timeFrame && (
          <Statistics
            timeFrame={timeFrame}
            businessUnit={businessUnit}
            dealOwnerId={dealOwnerId}
            onFetchingChange={setIsFetching}
          />
        )}
      </div>
    </>
  );
};
